import { useAuctionBids } from 'data/bids'
import { usePublishedProducts } from 'data/publishedProducts'
import { auctionStatuses } from 'constants/publishedProduct'
import { useMemo } from 'react'
import { chain, uniqBy, orderBy, sumBy, keyBy } from 'lodash'
import { biddedByValues, bidTypes } from 'constants/bid'
import { EU_COUNTRIES } from 'constants/index'
import { getMinimumRise } from 'utils/getMinimumRise'
import { useProductDynamic } from 'data/productDynamic'
import { useSellerCommissions } from 'data/sellerCommissions'
import { useWebshopOrders } from 'Components/Payments/Buyers/useWebshopOrders'
import { useProfilesMap } from './useProfilesMap'

export const useLatestAuctionStats = auctionId => {
  const { products, loading: productsLoading } = usePublishedProducts(auctionId)
  const { products: originalProducts } = useProductDynamic({
    queryKey: 'assignedAuctionId',
    queryValue: auctionId,
    queryTerm: '==',
  })
  const [bids, bidsLoading] = useAuctionBids(auctionId)

  const bidCount = useMemo(() => bids.length, [bids])
  const totalPublishedProducts = products.length
  const itemsWithBidsCount = uniqBy(bids, 'itemId').length
  const customersWithBidsCount = uniqBy(bids, 'userId').length
  const winningPreBidsSum = useMemo(
    () =>
      chain(bids)
        .filter(({ type }) => type === bidTypes.PRE)
        .groupBy('itemId')
        .reduce((sum, items, key) => {
          const [winningBid, secondHighest] = orderBy(items, 'amount', 'desc')

          const amount = winningBid.amount

          if (secondHighest && getMinimumRise(secondHighest.amount) + secondHighest.amount) {
            // If there is previous bid and it's too less then winning bid
            return sum + getMinimumRise(secondHighest.amount) + secondHighest.amount
          } else if (secondHighest) {
            // if there is second highest bid then we need to return amoumnt
            return sum + amount
          }

          const firstLiveSystemBid = orderBy(
            bids.filter(({ itemId, biddedBy }) => itemId === key && biddedBy === biddedByValues.SYSTEM),
            'amount',
            'asc'
          )[0]

          // First system bid always equals to hihhest prebid so if there is system bid return it
          if (firstLiveSystemBid) return sum + firstLiveSystemBid.amount

          const product = products.find(({ id }) => id === key)

          // If there is no system bid then return second highest as auction isn't started and highest prebid is seciond highest attribute of products
          return sum + product.secondHighestBid
        }, 0),
    [bids, products]
  )

  const winningLiveBidsSum = useMemo(
    () =>
      bids
        .filter(({ isWinning, type, invalid }) => !invalid && isWinning && type === bidTypes.LIVE)
        .reduce((sum, { amount }) => sum + amount, 0) +
      (originalProducts
        ?.filter(({ soldToUserId }) => !!soldToUserId)
        ?.reduce((sum, { finalAmount }) => sum + finalAmount, 0) ?? 0),
    [bids, originalProducts]
  )

  const originalById = useMemo(() => keyBy(originalProducts || [], 'id'), [originalProducts])

  const soldPublic = useMemo(() => (products || []).filter(p => p.auctionStatus === auctionStatuses.SOLD), [products])

  const winningLiveByItem = useMemo(() => {
    return bids.reduce((acc, b) => {
      if (b.isWinning && b.type === bidTypes.LIVE && !b.invalid) {
        const prev = acc[b.itemId]
        if (!prev || b.amount > prev.amount) acc[b.itemId] = b
      }
      return acc
    }, {})
  }, [bids])

  const soldDetails = useMemo(
    () =>
      soldPublic.map(pub => {
        const bid = winningLiveByItem[pub.id]
        const originalProduct = originalById[pub.id] || {}
        const price = bid?.amount ?? originalProduct.finalAmount ?? 0

        return {
          owner: originalProduct.owner,
          buyerId: bid?.userId,
          productId: pub.id,
          price,
        }
      }),
    [soldPublic, originalById, winningLiveByItem]
  )

  const unsoldPublic = useMemo(
    () => (products || []).filter(p => p.auctionStatus === auctionStatuses.NOT_SOLD),
    [products]
  )

  const unsoldDetails = useMemo(() => {
    return unsoldPublic.map(pub => {
      const originalProduct = originalById[pub.id] || {}
      const startPrice = pub?.technicalDetails?.AuctionMinimumPrice ?? 0

      return {
        owner: originalProduct.owner, // "holmasto" or "customer"
        price: startPrice,
      }
    })
  }, [unsoldPublic, originalById])

  const soldBuyerIds = useMemo(() => soldDetails.map(d => d.buyerId).filter(Boolean), [soldDetails])

  const profilesById = useProfilesMap(soldBuyerIds)

  const missingProfileIds = useMemo(() => soldBuyerIds.filter(id => !profilesById[id]), [soldBuyerIds, profilesById])

  const { sellerCommissions, loading: sellerCommissionsLoading } = useSellerCommissions(auctionId)

  const { orders, loading: buyerCommissionLoading } = useWebshopOrders(auctionId)

  const buyerCommissionTotal = useMemo(
    () =>
      sumBy(orders, order => {
        return sumBy(order.items, item => (item.price * (item.commission ?? order.commission)) / 100)
      }),
    [orders]
  )

  const customerDistribution = useMemo(() => {
    const counts = { fin: 0, eu: 0, other: 0 }
    soldDetails.forEach(({ buyerId }) => {
      const country = profilesById[buyerId]?.country
      if (!country) {
        counts.other++
      } else if (country === 'FI') {
        // How about Åland AX?
        counts.fin++
      } else if (EU_COUNTRIES.includes(country)) {
        counts.eu++
      } else {
        counts.other++
      }
    })
    return counts
  }, [soldDetails, profilesById])

  const sellerPayoutTotal = useMemo(() => sumBy(sellerCommissions, 'sum'), [sellerCommissions])

  const holmastoSold = soldDetails.filter(d => d.owner === 'holmasto')
  const customerSold = soldDetails.filter(d => d.owner === 'customer')

  const holmastoTotal = (originalProducts || []).filter(p => p.owner === 'holmasto').length
  const customerTotal = (originalProducts || []).filter(p => p.owner === 'customer').length

  const holmastoSalesTotal = useMemo(() => holmastoSold.reduce((sum, { price }) => sum + price, 0), [holmastoSold])

  const holmastoStats = useMemo(() => computeStats(holmastoSold, holmastoTotal), [holmastoSold, holmastoTotal])
  const customerStats = useMemo(() => computeStats(customerSold, customerTotal), [customerSold, customerTotal])

  const allStats = useMemo(
    () => computeStats(soldDetails, totalPublishedProducts),
    [soldDetails, totalPublishedProducts]
  )

  const holmastoUnsoldStats = useMemo(
    () =>
      computeStats(
        unsoldDetails.filter(d => d.owner === 'holmasto'),
        holmastoTotal
      ),
    [unsoldDetails, holmastoTotal]
  )

  const customerUnsoldStats = useMemo(
    () =>
      computeStats(
        unsoldDetails.filter(d => d.owner === 'customer'),
        customerTotal
      ),
    [unsoldDetails, customerTotal]
  )

  const allUnsoldStats = computeStats(unsoldDetails, totalPublishedProducts)

  const customerDistributionLoading = missingProfileIds.length > 0

  const loading = bidsLoading || productsLoading

  return {
    bidCount,
    totalPublishedProducts,
    itemsWithBidsCount,
    customersWithBidsCount,
    winningPreBidsSum,
    winningLiveBidsSum,
    holmastoStats,
    customerStats,
    allStats,
    customerDistribution,
    holmastoUnsoldStats,
    customerUnsoldStats,
    allUnsoldStats,
    holmastoSalesTotal,
    buyerCommissionTotal,
    sellerPayoutTotal,
    loading,
    sellerCommissionsLoading,
    buyerCommissionLoading,
    customerDistributionLoading,
  }
}

function computeStats(items = [], totalCount = 0) {
  const soldCount = items.length
  const totalSum = items.reduce((t, i) => t + i.price, 0)
  const avgPrice = soldCount > 0 ? totalSum / soldCount : 0
  return {
    soldCount,
    totalCount,
    avgPrice,
    totalSum,
  }
}
